import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import PageSeo from "../components/PageSeo";
import "./About.css";

const floatingIcons = ["🏍️", "🗺️", "🧭", "⛽", "🛡️", "🔧"];

const features = [
  {
    title: "Community Rides",
    body: "Weekend routes, city meetups, mountain runs, and long-haul plans from riders across India.",
  },
  {
    title: "Smart Ride Tools",
    body: "EMI, fuel, insurance, maintenance and trip tools built for Indian roads and real budgets.",
  },
  {
    title: "Route Intelligence",
    body: "Plan by distance, road type, fuel stops, and ride difficulty before your throttle opens.",
  },
  {
    title: "Safety First Stack",
    body: "Emergency support, trusted circles, and practical guidance to keep every ride safer.",
  },
];

const values = [
  "Safety-first riding culture",
  "Tools that reduce guesswork",
  "Respect for every rider skill level",
  "India-focused decisions and routes",
];

const tickerPills = [
  "Safety",
  "Community",
  "Route Planning",
  "Fuel Intelligence",
  "EMI Clarity",
  "Maintenance",
  "SOS Ready",
  "Rider-First Design",
];

const featureRoutes = [
  { title: "Rider Directory", body: "See rider profiles by city and state, then connect directly.", path: "/riders", cta: "Open Riders" },
  { title: "Create Blog Post", body: "Let riders publish real experiences, tips, and bike reviews.", path: "/blog/write", cta: "Write Blog" },
  { title: "Route Planning", body: "Discover riding routes and prep your next weekend run.", path: "/routes", cta: "View Routes" },
  { title: "Bike Compare", body: "Compare bikes quickly and move to tools for final decision.", path: "/compare", cta: "Compare Now" },
  { title: "Calculator Suite", body: "Use EMI, fuel, maintenance and more in one place.", path: "/calculator", cta: "Open Tools" },
  { title: "Community Blogs", body: "Read trending community stories and local riding insights.", path: "/blogs", cta: "Read Blogs" },
];

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.65, ease: "easeOut", delay }}
    >
      {children}
    </motion.div>
  );
}

function TiltCard({ title, body, delay }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    const rotateY = (px - 0.5) * 14;
    const rotateX = (0.5 - py) * 14;
    setTilt({ x: rotateX, y: rotateY });
  };

  return (
    <Reveal delay={delay}>
      <motion.article
        className="about-tilt-card"
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ x: 0, y: 0 })}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 240, damping: 20 }}
        style={{
          transformStyle: "preserve-3d",
          transform: `perspective(1100px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        }}
      >
        <h3>{title}</h3>
        <p>{body}</p>
      </motion.article>
    </Reveal>
  );
}

export default function About() {
  const { scrollY } = useScroll();
  const [solidNav, setSolidNav] = useState(false);

  const heroOrbY = useTransform(scrollY, [0, 600], [0, 180]);
  const heroContentY = useTransform(scrollY, [0, 600], [0, 70]);

  useMotionValueEvent(scrollY, "change", (value) => {
    setSolidNav(value > 36);
  });

  return (
    <>
      <PageSeo
        title="About MotoHub India | Rider Community, Tools, Routes & Safety"
        description="MotoHub India is a dark-mode rider-first platform for route planning, bike calculators, community rides, and safety tools built for India."
        keywords={[
          "MotoHub India",
          "motorcycle community India",
          "bike route planning",
          "bike EMI calculator",
          "fuel calculator India",
          "rider safety tools",
        ]}
        path="/about"
        image="/logo5.png"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About MotoHub India",
          description:
            "MotoHub India helps riders with profiles, blogs, routes, calculators, and community discovery across cities and states.",
          url: "https://motohubindia.com/about",
        }}
      />

      <div className="aboutx-root">
        <motion.div className="aboutx-nav" animate={{ backgroundColor: solidNav ? "rgba(8,8,8,0.85)" : "rgba(8,8,8,0)" }}>
          <div className="aboutx-nav-inner">
            <span className="aboutx-brand">MotoHub India</span>
            <Link className="aboutx-nav-link" to="/rides">
              Open Rides
            </Link>
          </div>
        </motion.div>

        <motion.div className="aboutx-orb aboutx-orb-amber" animate={{ y: [0, -30, 0], x: [0, 18, 0] }} transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="aboutx-orb aboutx-orb-blue" animate={{ y: [0, 26, 0], x: [0, -16, 0] }} transition={{ duration: 6.8, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="aboutx-orb aboutx-orb-orange" animate={{ y: [0, -22, 0], x: [0, 12, 0] }} transition={{ duration: 8.2, repeat: Infinity, ease: "easeInOut" }} />

        <section className="aboutx-hero">
          <motion.div className="aboutx-hero-glow" style={{ y: heroOrbY }} />

          {floatingIcons.map((icon, index) => (
            <motion.span
              key={icon + index}
              className="aboutx-float-icon"
              style={{
                left: `${8 + index * 14}%`,
                top: `${12 + (index % 3) * 22}%`,
              }}
              animate={{ y: [0, -40, -20, -60, 0] }}
              transition={{
                duration: 8 + index * 0.7,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              {icon}
            </motion.span>
          ))}

          <motion.div className="aboutx-hero-content" style={{ y: heroContentY }}>
            <p className="aboutx-kicker">Built for Indian riders</p>
            <h1>Where bikes, riders, and real planning come together.</h1>
            <p>
              MotoHub India combines community rides, route insights, ride budgeting, and safety-focused tools into one powerful rider experience.
            </p>

            <div className="aboutx-cta-row">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link to="/recommend" className="aboutx-btn aboutx-btn-primary">
                  Try AI Bike Finder
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link to="/feedback" className="aboutx-btn aboutx-btn-ghost">
                  Share Feedback
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </section>

        <section className="aboutx-wrap">
          <Reveal>
            <div className="aboutx-head">
              <p className="aboutx-kicker">Platform Pillars</p>
              <h2>Designed to feel premium, useful, and fast.</h2>
            </div>
          </Reveal>

          <div className="aboutx-grid">
            {features.map((feature, index) => (
              <TiltCard key={feature.title} title={feature.title} body={feature.body} delay={index * 0.15} />
            ))}
          </div>
        </section>

        <section className="aboutx-wrap aboutx-columns">
          <Reveal>
            <article className="aboutx-panel">
              <p className="aboutx-kicker">Our Mission</p>
              <h3>Make every bike decision clearer.</h3>
              <p>
                We are building a rider operating system for India. From selecting the right bike to managing long-term ownership costs, every feature should save time and increase confidence.
              </p>
            </article>
          </Reveal>

          <Reveal delay={0.15}>
            <article className="aboutx-panel">
              <p className="aboutx-kicker">Our Values</p>
              <ul className="aboutx-checklist">
                {values.map((value) => (
                  <li key={value}>
                    <motion.span whileHover={{ scale: 1.1, rotate: -8 }} className="aboutx-check">
                      ✓
                    </motion.span>
                    {value}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        </section>

        <section className="aboutx-wrap">
          <Reveal>
            <div className="aboutx-marquee-shell">
              <div className="aboutx-marquee-fade aboutx-marquee-fade-left" />
              <div className="aboutx-marquee-fade aboutx-marquee-fade-right" />
              <div className="aboutx-marquee-track">
                {[...tickerPills, ...tickerPills].map((pill, index) => (
                  <span className="aboutx-pill" key={pill + index}>
                    {pill}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </section>

        <section className="aboutx-wrap">
          <Reveal>
            <article className="aboutx-panel aboutx-join">
              <p className="aboutx-kicker">Ride With Us</p>
              <h3>Help shape the next generation of MotoHub India.</h3>
              <p>Tell us what riders need next, then watch it ship.</p>

              <div className="aboutx-inline-links">
                <motion.div whileHover={{ x: 3 }}>
                  <Link to="/feedback" className="aboutx-inline-link">
                    Open Feedback <span>→</span>
                  </Link>
                </motion.div>
                <motion.div whileHover={{ x: 3 }}>
                  <Link to="/blogs" className="aboutx-inline-link">
                    Read Rider Stories <span>→</span>
                  </Link>
                </motion.div>
              </div>
            </article>
          </Reveal>
        </section>

        <section className="aboutx-wrap">
          <Reveal>
            <div className="aboutx-head">
              <p className="aboutx-kicker">Use Every Feature</p>
              <h2>Jump directly to the pages riders use most.</h2>
            </div>
          </Reveal>
          <div className="aboutx-grid">
            {featureRoutes.map((item, index) => (
              <Reveal key={item.title} delay={index * 0.08}>
                <article className="aboutx-tilt-card">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  <Link to={item.path} className="aboutx-inline-link" style={{ display: "inline-block", marginTop: 10 }}>
                    {item.cta} <span>→</span>
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
